import { z } from "zod";

/**
 * External System Validation Schemas
 *
 * Validation schemas for external system connector forms and operations
 * Backend-aligned schemas that ensure type safety with generated API types
 */

/**
 * Username validation schema
 * Standard validation for external system usernames
 */
export const externalSystemUsernameSchema = z
  .string()
  .min(1, "Username is required")
  .max(255, "Username must be less than 255 characters")
  .trim();

/**
 * Password validation schema
 * Standard validation for external system passwords
 */
export const externalSystemPasswordSchema = z
  .string()
  .min(1, "Password is required")
  .max(255, "Password must be less than 255 characters");

/**
 * Schema for creating a new external system (superuser only)
 * Matches backend ExternalSystemCreate
 */
export const externalSystemCreateSchema = z.object({
  name: z
    .string()
    .min(1, "System name is required")
    .max(255, "System name must be less than 255 characters")
    .trim(),
  base_url: z
    .string()
    .max(500, "Base URL must be less than 500 characters")
    .trim()
    .refine(
      (val) => !val || z.string().url().safeParse(val).success,
      "Base URL must be a valid URL",
    )
    .optional(),
});

/**
 * Schema for updating an external system (superuser only)
 * Matches backend ExternalSystemUpdate
 */
export const externalSystemUpdateSchema = externalSystemCreateSchema.partial();

/**
 * Schema for creating a new external system connector.
 * The UI intentionally requires username/password even though the backend
 * allows them to be null -- credentials should always be provided on creation.
 */
export const externalSystemConnectorCreateSchema = z.object({
  external_system_id: z.string().uuid("Invalid external system ID"),
  is_active: z.boolean(),
  username: externalSystemUsernameSchema,
  password: externalSystemPasswordSchema,
});

/**
 * Schema for updating an external system connector
 * Matches backend ExternalSystemConnectorUpdate
 */
export const externalSystemConnectorUpdateSchema = z
  .object({
    is_active: z.boolean().optional(),
    username: externalSystemUsernameSchema.optional(),
    password: externalSystemPasswordSchema.optional(),
  })
  .refine(
    (data) => Object.keys(data).length > 0,
    "At least one field must be provided for update",
  );

/**
 * Assign a catalog system to a client.
 * `is_active` is omitted so the API default (true) applies.
 */
export const assignClientExternalSystemSchema = z.object({
  external_system_id: z.string().uuid("Invalid external system ID"),
});

const externalSystemNameSchema = z.object({
  uuid: z.string().uuid(),
  name: z.string().min(1),
});

const assignedByUserSchema = z.object({
  uuid: z.string().uuid(),
  name: z.string().min(1),
});

/** 201 body from POST /v1/external-systems/client/{client_id}. */
export const clientExternalSystemReadSchema = z.object({
  uuid: z.string().uuid(),
  client_id: z.string().uuid(),
  is_active: z.boolean(),
  external_system: externalSystemNameSchema,
  assigned_by_user: assignedByUserSchema,
});

/** 200 body from DELETE assignment. */
export const assignmentMessageResponseSchema = z.object({
  message: z.string(),
});

// --- Auto-Push Configuration Schemas ---

export const autoPushConfigCreateSchema = z.object({
  external_system_id: z.string().uuid("Invalid external system ID"),
  location_id: z.string().uuid("Invalid location ID"),
  aggregate_id: z.string().uuid("Invalid aggregate ID"),
  analysis_type_id: z.string().uuid("Invalid analysis type ID"),
  is_enabled: z.boolean().default(true),
});

export const autoPushConfigBulkUpdateItemSchema = z.object({
  uuid: z.string().uuid("Invalid config ID"),
  is_enabled: z.boolean(),
});

export const autoPushConfigBulkCreateSchema = z.object({
  configs: z
    .array(autoPushConfigCreateSchema)
    .min(1, "At least one config is required")
    .max(100, "Maximum 100 configs per request"),
});

export const autoPushConfigBulkUpdateSchema = z.object({
  configs: z
    .array(autoPushConfigBulkUpdateItemSchema)
    .min(1, "At least one config is required")
    .max(100, "Maximum 100 configs per request"),
});

// Type inference for external system schemas
export type ExternalSystemCreateForm = z.infer<
  typeof externalSystemCreateSchema
>;
export type ExternalSystemUpdateForm = z.infer<
  typeof externalSystemUpdateSchema
>;
export type ExternalSystemConnectorCreateForm = z.infer<
  typeof externalSystemConnectorCreateSchema
>;
export type ExternalSystemConnectorUpdateForm = z.infer<
  typeof externalSystemConnectorUpdateSchema
>;
export type AssignClientExternalSystemInput = z.infer<
  typeof assignClientExternalSystemSchema
>;
export type AutoPushConfigCreateForm = z.infer<
  typeof autoPushConfigCreateSchema
>;
export type AutoPushConfigBulkCreateForm = z.infer<
  typeof autoPushConfigBulkCreateSchema
>;
export type AutoPushConfigBulkUpdateForm = z.infer<
  typeof autoPushConfigBulkUpdateSchema
>;
